<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { capitalize, formatNumberDisplayByLang } from '@/helpers/formatters'
import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'
import { AdsButton, AdsIcon } from '@accor/ads-components'
import { IconAccount } from '@accor/icons'
import StatusNightCardIcon from '@/assets/icons/status-night-card.svg'
import ArrowSwitchIcon from '@/assets/icons/arrow-switch.svg'
import IconStarCircle from '@/components/atoms/icons/IconStarCircle.vue'
import bellIcon from '@/assets/icons/bell.svg'
import { useRouter } from 'vue-router'

const { t } = useI18n()
const router = useRouter()
const { firstName, productTier, rewardsPoints, balances, hasGuestNotifications } =
  storeToRefs(useUserStore())

const tier = computed(() => productTier.value)
const statusPoints = computed(() => balances.value.nbPointsEarnLast12Months)
const currentNights = computed(() => balances.value.currentNightsBalance)

const isB2B = ref(false) // temporary for B2B check

const switchMode = () => {
  isB2B.value = !isB2B.value
}
</script>

<template>
  <div class="user-card">
    <div class="user-card__info">
      <div class="user-card__text">
        <p v-if="isB2B" class="user-card__greeting" data-cs-mask>Nom de l'entreprise tmp</p>
        <i18n-t v-else keypath="welcome" tag="p" class="user-card__greeting">
          <template #firstname>
            <span data-cs-mask>{{ capitalize(firstName) }}</span>
          </template>
        </i18n-t>

        <p v-if="isB2B" class="user-card__status">Espace entreprise tmp</p>
        <p v-else class="user-card__status">
          {{ t('navigation.loyalty.tier') }}
          <span
            class="user-card__status-value"
            :class="`user-card__status-value--${tier.toLowerCase()}`"
            >{{ tier }}</span
          >
        </p>
      </div>
      <div class="user-card__notification">
        <AdsButton
          variant="secondary"
          size="medium"
          :aria-label="t('navigation.entry.notifcationCenter')"
          @click="router.push({ name: 'dashboard' })"
          icon-only
          circle
          :prepend-icon="{ icon: bellIcon, size: 'xs' }"
        />
        <span v-if="hasGuestNotifications" class="user-card__notification-badge" />
      </div>
    </div>
    <div v-if="!isB2B" class="user-card__stat">
      <AdsIcon :icon="StatusNightCardIcon" size="sm" fill="none" />
      <i18n-t keypath="navigation.header.statusPointsAndNights" tag="p">
        <template #statusPoints>
          <span class="user-card__stat-value">{{ formatNumberDisplayByLang(statusPoints) }}</span>
        </template>
        <template #nights>
          <span class="user-card__stat-value">{{ formatNumberDisplayByLang(currentNights) }}</span>
        </template>
      </i18n-t>
    </div>
    <hr class="user-card__separator" />
    <div class="user-card__stat">
      <AdsIcon :icon="IconStarCircle" size="sm" fill="none" />
      {{
        t('navigation.header.rewardPoints', { reward: formatNumberDisplayByLang(rewardsPoints) })
      }}
    </div>
    <div v-if="isB2B" role="button" class="user-card__switch" @click="switchMode">
      {{ isB2B ? t('navigationBar.switchToB2C') : t('navigationBar.switchToB2B') }}
      <AdsIcon :icon="isB2B ? IconAccount : ArrowSwitchIcon" size="sm" fill="none" />
    </div>
  </div>
</template>

<style lang="scss">
@use '@/styles/variables' as *;

.user-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ads.$ads-spacing-scale-sm;
  padding: ads.$ads-spacing-scale-lg;
  background-color: ads.$ads-primary-container-low;
  border-radius: ads.$ads-border-radius-container-low;

  &__info {
    display: flex;
    flex-direction: row;
    align-self: stretch;
    gap: ads.$ads-spacing-scale-md;
  }

  &__text {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  &__notification {
    position: relative;
    margin-left: auto;
  }

  &__notification-badge {
    position: absolute;
    top: -2px;
    right: 0px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: ads.$ads-on-danger-container-low;
    border: 1px solid ads.$ads-surface;
    pointer-events: none;
  }

  &__greeting {
    @include ads-sass.title-03;
    color: ads.$ads-primary;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__status {
    @include ads-sass.body-default-02;
    color: ads.$ads-primary;

    &-value {
      @include ads-sass.body-strong-02;

      &--classic {
        color: ads.$ads-classic;
      }
      &--silver {
        color: ads.$ads-silver;
      }
      &--gold {
        color: ads.$ads-gold;
      }
      &--platinum {
        color: ads.$ads-platinum;
      }
      &--diamond {
        color: ads.$ads-diamond;
      }
      &--limitless {
        color: ads.$ads-limitless;
      }
    }
  }

  &__stat {
    display: flex;
    flex-direction: row;
    align-items: center;
    align-self: stretch;
    gap: ads.$ads-spacing-scale-xs;
    @include ads-sass.body-default-02;
    color: ads.$ads-primary;

    &-value {
      @include ads-sass.body-strong-02;
    }
  }

  &__separator {
    width: 100%;
    border: none;
    border-top: 1px solid ads.$ads-loyalty-container-hi;
    margin: 0;
  }

  &__switch {
    display: flex;
    flex-direction: row;
    align-items: center;
    align-self: stretch;
    gap: ads.$ads-spacing-scale-xs;
    padding-top: ads.$ads-spacing-scale-sm;
    @include ads-sass.link-alternative-01;
    color: ads.$ads-link;
    cursor: pointer;
  }
}
</style>
